import React, { useEffect, useRef, useState } from "react";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import { useDocumentUpload } from "@/screen/Identities/IdentityDocumentUpload/useDocumentUpload";
import type { UseDocumentUploadCallbacks } from "@/screen/Identities/IdentityDocumentUpload/useDocumentUpload";
import { IdentityDocumentUploadProps } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import IdentityAttachmentPreview from "@/screen/Identities/components/IdentityAttachmentPreview";
import { FormSection, FormLabel, FormInput, DocumentUploadSection, DocumentUploadRow, DocumentUploadField, DocumentPreview, DocumentPlaceholder, DocumentSectionTitle, DocumentSectionDescription, DocumentFormatHint, ProcessButtonRow, DniManualBanner, DniManualBannerTitle, DniManualBannerSubtitle, DniManualInputRow, DniManualInputCol, DniManualInputError, DniManualConfirmButton, DocumentProcessedMessage } from "@/screen/Identities/Styles/IdentityFormStyles";

const IdentityDocumentUpload: React.FC<IdentityDocumentUploadProps> = ({mode, userIdentityId, initialFrontPreview, initialBackPreview, initialIdDocument, canRenewIdentityDocument, isMissingIdentityDocuments, isStoredDocumentExpired, isStoredIdExpirationMissing, hasStoredDocuments, onDocumentProcessed, onServerWarning, onError, onAttachmentErrors, onPreviewLoadingChange, onDniConfirmed, onFlowResolved}) =>
{
    const callbacks: UseDocumentUploadCallbacks =
    {
        onDocumentProcessed,
        onServerWarning,
        onError,
        onDniConfirmed,
    };

    const {state, handleDocumentChange, handleProcessDocument, handleManualDniChange, handleManualDniConfirm} = useDocumentUpload(mode, userIdentityId, initialFrontPreview, initialBackPreview, initialIdDocument, callbacks, canRenewIdentityDocument, hasStoredDocuments);
    const [frontAttachmentError, setFrontAttachmentError] = useState<string | null>(null);
    const [backAttachmentError, setBackAttachmentError] = useState<string | null>(null);
    const [frontPreviewLoading, setFrontPreviewLoading] = useState<boolean>(false);
    const [backPreviewLoading, setBackPreviewLoading] = useState<boolean>(false);

    // Cuando el hook interno resuelve el flujo, notificamos al controller.
    // Usamos una ref para onFlowResolved para evitar disparar el efecto en cada render del padre.
    const onFlowResolvedRef = useRef(onFlowResolved);
    onFlowResolvedRef.current = onFlowResolved;

    useEffect(() =>
    {
        if(state.phase === "flow_resolved")
        {
            onFlowResolvedRef.current(state.resolvedAction, state.identityStatus);
        }
    }, [state.phase, state.resolvedAction, state.identityStatus]);

    useEffect(() =>
    {
        const errors = [];
        if(frontAttachmentError)
        {
            errors.push({field: "Documento. Anverso Documento de Identidad", message: frontAttachmentError});
        }
        if(backAttachmentError)
        {
            errors.push({field: "Documento. Reverso Documento de Identidad", message: backAttachmentError});
        }
        onAttachmentErrors(errors);
    }, [frontAttachmentError, backAttachmentError, onAttachmentErrors]);

    useEffect(() =>
    {
        onPreviewLoadingChange(frontPreviewLoading || backPreviewLoading);
    }, [frontPreviewLoading, backPreviewLoading, onPreviewLoadingChange]);

    const isEditMode = mode === "edit";
    const isUploading = state.phase === "uploading";
    const isAnalyzingStatus = state.phase === "analyzing_status";
    const isProcessing = isUploading || isAnalyzingStatus;
    const isDniRequired = state.phase === "dni_required";
    const analysisSucceeded = state.phase === "flow_resolved" && !isEditMode;
    // En edición, los inputs de documento están habilitados para los estados que permiten renovación
    // (ACTIVE, OUTDATED, PENDING, y NEW con el documento caducado); en el resto permanecen deshabilitados.
    const isDocumentInputDisabled = isProcessing || (isEditMode && !canRenewIdentityDocument);

    const documentSectionDescription = isEditMode ? isDniRequired
            ? "Este interviniente tiene documento de identidad adjunto pero falta el número de DNI/NIE. Introdúcelo manualmente para poder completar el alta."
            : isMissingIdentityDocuments
                ? "Faltan las imágenes del documento de identidad. Sube el anverso y el reverso para poder finalizar el alta. Los datos del formulario no se modificarán."
                : canRenewIdentityDocument
                    // Que el estado permita sustituir el documento no significa que esté caducado: solo se
                    // anuncia la caducidad cuando lo dice la fecha de validez persistida. Sin fecha persistida
                    // tampoco se puede afirmar que esté en vigor: se pide rellenarla, que es lo que exige el guardado.
                    ? isStoredDocumentExpired
                        ? "El documento de identidad ha caducado. Para guardar los cambios, sube las imágenes del anverso y reverso del documento renovado y actualiza manualmente la fecha de validez en el formulario."
                        : isStoredIdExpirationMissing
                            ? "No consta la fecha de validez de tu documento de identidad. Rellénala en el formulario para poder guardar los cambios. Si has renovado el documento, puedes subir también las nuevas imágenes del anverso y reverso."
                            : "Tu documento de identidad está en vigor. Si lo has renovado, puedes subir las nuevas imágenes del anverso y reverso y actualizar manualmente la fecha de validez en el formulario."
                    : "Documento de identidad asociado al interviniente."
        : "Sube las imágenes del anverso y reverso de tu DNI/NIE. Los datos se extraerán automáticamente. Aun así, debes validarlos y verificar que todos son correctos.";

    return(
        <FormSection>
            <DocumentSectionTitle>Documento de Identidad</DocumentSectionTitle>
            <DocumentSectionDescription>
                {documentSectionDescription}
            </DocumentSectionDescription>
            <DocumentUploadSection>
                <DocumentUploadRow>
                    <DocumentUploadField>
                        <FormLabel required>DNI / NIE parte delantera</FormLabel>
                        <FormInput
                            type="file"
                            id="idFront"
                            name="idFront"
                            accept=".png,.jpg,.jpeg,.pdf"
                            onChange={(e) => handleDocumentChange(e.target.files?.[0] ?? null, "front")}
                            disabled={isDocumentInputDisabled}
                            autoComplete="off"
                        />
                        <DocumentFormatHint>Formatos: .png, .jpg, .jpeg, .pdf</DocumentFormatHint>
                        <DocumentPreview>
                            <IdentityAttachmentPreview
                                source={state.idFrontFile ?? state.idFrontPreview}
                                alt="Anverso DNI"
                                placeholder={<DocumentPlaceholder><span>Anverso DNI</span></DocumentPlaceholder>}
                                onErrorChange={setFrontAttachmentError}
                                onLoadingChange={setFrontPreviewLoading}
                            />
                        </DocumentPreview>
                    </DocumentUploadField>
                    <DocumentUploadField>
                        <FormLabel required>DNI / NIE parte trasera</FormLabel>
                        <FormInput type="file" id="idBack" name="idBack"
                            accept=".png,.jpg,.jpeg,.pdf"
                            onChange={(e) => handleDocumentChange(e.target.files?.[0] ?? null, "back")}
                            disabled={isDocumentInputDisabled}
                            autoComplete="off"
                        />
                        <DocumentFormatHint>Formatos: .png, .jpg, .jpeg, .pdf</DocumentFormatHint>
                        <DocumentPreview>
                            <IdentityAttachmentPreview
                                source={state.idBackFile ?? state.idBackPreview}
                                alt="Reverso DNI"
                                placeholder={<DocumentPlaceholder><span>Reverso DNI</span></DocumentPlaceholder>}
                                onErrorChange={setBackAttachmentError}
                                onLoadingChange={setBackPreviewLoading}
                            />
                        </DocumentPreview>
                    </DocumentUploadField>
                </DocumentUploadRow>
                {/* Botón de procesado: visible solo mientras el documento no ha sido procesado */}
                {!isEditMode && state.phase === "idle" &&
                (
                    <ProcessButtonRow>
                        <DefaultButton className="button tiny-primary-button" onClick={handleProcessDocument}
                            disabled={!state.idFrontFile || !state.idBackFile}
                        >
                            Procesar documento
                        </DefaultButton>
                    </ProcessButtonRow>
                )}
                {/* Spinner mientras se sube o se analiza */}
                {isProcessing &&
                (
                    <ProcessButtonRow>
                        <Spinner size={SpinnerSize.small} label={isUploading ? "Procesando documento..." : "Verificando interviniente..."} />
                    </ProcessButtonRow>
                )}
                {/* Paso extra: el análisis no pudo extraer el DNI */}
                {isDniRequired &&
                (
                    <DniManualBanner>
                        <DniManualBannerTitle>
                            No se pudo detectar el número de documento automáticamente.
                        </DniManualBannerTitle>
                        <DniManualBannerSubtitle>
                            Introduce el número de DNI o NIE manualmente para continuar con el procedimiento.
                        </DniManualBannerSubtitle>
                        <DniManualInputRow>
                            <DniManualInputCol>
                                <FormLabel required>Número de documento</FormLabel>
                                <FormInput type="text" id="manualDni" name="manualDni"
                                    value={state.manualDniValue}
                                    placeholder="Ej: 12345678A"
                                    autoComplete="off"
                                    onChange={(e) => handleManualDniChange(e.target.value)}
                                    onKeyDown={(e) => {if(e.key === "Enter") {handleManualDniConfirm()}}}
                                    style={{width: "180px"}}
                                />
                                {state.manualDniError &&
                                (
                                    <DniManualInputError>{state.manualDniError}</DniManualInputError>
                                )}
                            </DniManualInputCol>
                            <DniManualConfirmButton type="button" onClick={handleManualDniConfirm}>
                                Confirmar
                            </DniManualConfirmButton>
                        </DniManualInputRow>
                    </DniManualBanner>
                )}
                {analysisSucceeded &&
                (
                    <DocumentProcessedMessage>
                        Documento procesado. Revisa y completa los datos del formulario.
                    </DocumentProcessedMessage>
                )}
            </DocumentUploadSection>
        </FormSection>
    );
};

export default IdentityDocumentUpload;